
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { uploadToCOS, uploadThumbnailToCOS } from "@/lib/cos-upload";
import { useToast } from "@/hooks/use-toast";

async function readResponsePayload(response: unknown): Promise<string | null> {
  const r = response as { clone?: () => Response; text?: () => Promise<string> } | undefined;
  const res = r?.clone ? r.clone() : (r as unknown as Response | undefined);
  if (!res?.text) return null;
  try {
    const text = await res.text();
    if (!text) return null;
    try {
      const json = JSON.parse(text) as unknown;
      const message =
        (json as { error?: unknown })?.error ??
        (json as { message?: unknown })?.message ??
        (json as { msg?: unknown })?.msg;
      if (typeof message === "string" && message.trim()) return message.trim();
      return text.length > 800 ? text.slice(0, 800) + "…" : text;
    } catch {
      return text.length > 800 ? text.slice(0, 800) + "…" : text;
    }
  } catch {
    return null;
  }
}

function stringifyUnknownBody(body: unknown): string | null {
  if (body == null) return null;
  if (typeof body === "string") return body.trim() || null;
  if (typeof body === "number" || typeof body === "boolean") return String(body);
  try {
    return JSON.stringify(body);
  } catch {
    return null;
  }
}

function extractMessageFromJsonLike(value: unknown): string | null {
  const v = value as { error?: unknown; message?: unknown; msg?: unknown } | undefined;
  const message = v?.error ?? v?.message ?? v?.msg;
  if (typeof message === "string" && message.trim()) return message.trim();
  return null;
}

async function formatUploadError(err: unknown): Promise<string> {
  const e = err as {
    message?: unknown;
    name?: unknown;
    code?: unknown;
    details?: unknown;
    hint?: unknown;
    context?: unknown;
    cause?: unknown;
  };

  const base = typeof e?.message === "string" && e.message.trim() ? e.message.trim() : "上传失败";
  const code = typeof e?.code === "string" && e.code.trim() ? e.code.trim() : null;
  const details = typeof e?.details === "string" && e.details.trim() ? e.details.trim() : null;
  const hint = typeof e?.hint === "string" && e.hint.trim() ? e.hint.trim() : null;
  const ctx = e?.context as { response?: unknown; status?: unknown; statusText?: unknown; body?: unknown } | undefined;
  const response = ctx?.response;
  const responsePayload = response ? await readResponsePayload(response) : null;
  const statusFromResponse = (response as { status?: unknown } | undefined)?.status;
  const statusTextFromResponse = (response as { statusText?: unknown } | undefined)?.statusText;
  const status = typeof statusFromResponse === "number" ? statusFromResponse : (typeof ctx?.status === "number" ? ctx.status : undefined);
  const statusText =
    typeof statusTextFromResponse === "string"
      ? statusTextFromResponse
      : typeof ctx?.statusText === "string"
        ? ctx.statusText
        : undefined;

  const ctxBodyText = stringifyUnknownBody(ctx?.body);
  let ctxBodyMessage: string | null = null;
  if (ctxBodyText) {
    try {
      ctxBodyMessage = extractMessageFromJsonLike(JSON.parse(ctxBodyText));
    } catch {
      ctxBodyMessage = null;
    }
  }
  const bodyMessage = ctxBodyMessage || ctxBodyText || responsePayload;

  const parts: string[] = [];
  if (code) parts.push(code);
  if (typeof status === "number") {
    parts.push(statusText ? `${status} ${String(statusText)}` : String(status));
  }
  if (bodyMessage) parts.push(bodyMessage);
  if (details) parts.push(details);
  if (hint) parts.push(hint);

  const suffix = parts.length ? `（${parts.join(" / ")}）` : "";

  const cause = e?.cause;
  if (cause && cause !== err) {
    const causeMsg = await formatUploadError(cause);
    if (causeMsg && causeMsg !== base) return `${base}${suffix}；${causeMsg}`;
  }

  return `${base}${suffix}`;
}

interface VttCue {
  startTime: number;
  endTime: number;
  text: string;
}

async function getMediaDurationFromFile(file: File, timeoutMs = 12000): Promise<number> {
  // m3u8 local file cannot be probed reliably without its segment files.
  if (file.name.toLowerCase().endsWith(".m3u8")) return 0;

  return new Promise<number>((resolve) => {
    const el = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);
    let settled = false;
    let timeoutId: number | undefined;

    const cleanup = () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      try {
        el.removeAttribute("src");
        el.load();
      } catch {
        // no-op
      }
      URL.revokeObjectURL(objectUrl);
    };

    const finish = (duration: number) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(Number.isFinite(duration) && duration > 0 ? Math.round(duration) : 0);
    };

    el.preload = "metadata";
    el.muted = true;
    el.onloadedmetadata = () => finish(el.duration);
    el.onerror = () => finish(0);
    el.onabort = () => finish(0);
    el.onstalled = () => finish(0);

    timeoutId = window.setTimeout(() => finish(0), timeoutMs);

    el.src = objectUrl;
    try {
      el.load();
    } catch {
      finish(0);
    }
  });
}

function parseVttTime(timeStr: string): number {
  const parts = timeStr.split(":");
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (parts.length === 3) {
    hours = parseInt(parts[0], 10);
    minutes = parseInt(parts[1], 10);
    seconds = parseFloat(parts[2]);
  } else if (parts.length === 2) {
    minutes = parseInt(parts[0], 10);
    seconds = parseFloat(parts[1]);
  }

  return hours * 3600 + minutes * 60 + seconds;
}

function parseVTT(content: string): VttCue[] {
  const cues: VttCue[] = [];
  const lines = content.split("\n");
  let i = 0;

  while (i < lines.length && !lines[i].includes("-->")) {
    i++;
  }

  while (i < lines.length) {
    const line = lines[i].trim();
    if (line.includes("-->")) {
      const [startStr, endStr] = line.split("-->").map((s) => s.trim());
      const startTime = parseVttTime(startStr);
      const endTime = parseVttTime(endStr);

      const textLines: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() && !lines[i].includes("-->")) {
        if (!/^\d+$/.test(lines[i].trim())) {
          textLines.push(lines[i].trim());
        }
        i++;
      }

      if (textLines.length > 0) {
        cues.push({
          startTime,
          endTime,
          text: textLines.join("\n"),
        });
      }
    } else {
      i++;
    }
  }

  return cues;
}

function isRetryableEdgeFunctionError(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("failed to send a request to the edge function") ||
    normalized.includes("edge function returned a non-2xx status code") ||
    normalized.includes("gateway timeout") ||
    normalized.includes("504") ||
    normalized.includes("timeout") ||
    normalized.includes("network")
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function saveRawVttSubtitles(videoId: string, cues: VttCue[]): Promise<void> {
  const { error: deleteError } = await supabase
    .from("subtitles")
    .delete()
    .eq("video_id", videoId);
  if (deleteError) throw deleteError;

  const records = cues.map((cue, index) => ({
    video_id: videoId,
    start_time: cue.startTime,
    end_time: cue.endTime,
    japanese: cue.text,
    // Fallback path: keep readable subtitles first, translate later by background job.
    chinese: cue.text,
    romaji: null,
    furigana: null,
    sort_order: index,
  }));

  const insertChunkSize = 200;
  for (let i = 0; i < records.length; i += insertChunkSize) {
    const chunk = records.slice(i, i + insertChunkSize);
    const { error: insertError } = await supabase
      .from("subtitles")
      .insert(chunk);
    if (insertError) throw insertError;
  }

  const estimatedDuration = Math.ceil(cues[cues.length - 1].endTime);
  const { error: updateError } = await supabase
    .from("videos")
    .update({ duration: estimatedDuration })
    .eq("id", videoId);
  if (updateError) throw updateError;
}

// --- Types ---

type UploadTaskStatus = 'queued' | 'pending' | 'uploading' | 'processing' | 'completed' | 'error';

export interface UploadTask {
  id: string; // Unique ID for the task
  file: File;
  thumbnailFile?: File;
  vttFile?: File; // Optional subtitle file
  metadata: {
    title: string;
    description: string;
    jlpt_level: string; // Keep for compatibility
    jlpt_levels?: string[]; // New
    category: string; // Keep for compatibility
    categories?: string[]; // New
    series_id: string; // "none" or uuid
    is_premium: boolean;
    is_published: boolean;
    autoDetectLevel: boolean;
  };
  status: UploadTaskStatus;
  progress: number; // 0-100
  error?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  logs?: string[];
}

interface UploadContextType {
  tasks: UploadTask[];
  addTask: (task: Omit<UploadTask, 'id' | 'status' | 'progress'>) => void;
  startTask: (id: string) => void;
  startAll: () => void;
  removeTask: (id: string) => void;
  retryTask: (id: string) => void;
  clearCompleted: () => void;
  isUploading: boolean;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export function UploadProvider({ children }: { children: React.ReactNode }) {
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [autoStart, setAutoStart] = useState(false);
  const currentUploadRef = useRef<string | null>(null);
  const { toast } = useToast();

  // --- Task Management ---

  const addTask = useCallback((taskData: Omit<UploadTask, 'id' | 'status' | 'progress'>) => {
    const now = new Date().toLocaleString("zh-CN", { hour12: false });
    const newTask: UploadTask = {
      ...taskData,
      id: crypto.randomUUID(),
      status: 'queued',
      progress: 0,
      logs: [`[${now}] 任务已创建，等待开始`],
    };
    setTasks(prev => [...prev, newTask]);
  }, []);

  const startTask = useCallback((id: string) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id && t.status === "queued" ? { ...t, status: "pending" } : t)),
    );
    setAutoStart(true);
  }, []);

  const startAll = useCallback(() => {
    setTasks((prev) => prev.map((t) => (t.status === "queued" ? { ...t, status: "pending" } : t)));
    setAutoStart(true);
  }, []);

  const removeTask = useCallback((id: string) => {
    setTasks(prev => prev.filter(t => t.id !== id));
    if (currentUploadRef.current === id) {
      currentUploadRef.current = null;
      setIsUploading(false);
    }
  }, []);

  const retryTask = useCallback((id: string) => {
    const now = new Date().toLocaleString("zh-CN", { hour12: false });
    setTasks(prev => prev.map(t =>
      t.id === id
        ? {
            ...t,
            status: 'queued',
            progress: 0,
            error: undefined,
            logs: [...(t.logs || []), `[${now}] 手动重试`],
          }
        : t
    ));
  }, []);

  const clearCompleted = useCallback(() => {
    setTasks(prev => prev.filter(t => t.status !== 'completed'));
  }, []);

  const updateTask = (id: string, updates: Partial<UploadTask>) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  };

  // --- Upload Processor ---

  const processNextTask = useCallback(async () => {
    if (isUploading || currentUploadRef.current) return;

    const nextTask = tasks.find(t => t.status === 'pending');
    if (!nextTask) return;

    setIsUploading(true);
    currentUploadRef.current = nextTask.id;
    updateTask(nextTask.id, { status: 'uploading', progress: 0 });

    // Initialize logs
    const startTime = new Date();
    const requestedPublishState = !!nextTask.metadata.is_published;
    let createdVideoId: string | null = null;
    const logs: string[] = [];
    const syncLogs = () => {
      updateTask(nextTask.id, { logs: [...logs] });
    };
    const addLog = (step: string, status: "WAIT" | "RUNNING" | "SUCCESS" | "SKIP" | "DONE" | "ERROR", msg: string = "") => {
      const time = new Date().toLocaleString('zh-CN', { hour12: false });
      logs.push(`[${time}] ${step}: [${status}] ${msg}`);
      syncLogs();
    };

    logs.push("[INFO] --- 上传流水线日志 ---");
    syncLogs();

    try {
      // Step 1: Upload Video
      addLog("第 1 步 (媒体素材)", "WAIT", "准备中");
      
      const isM3U8 = nextTask.file.name.toLowerCase().endsWith('.m3u8');
      
      // For HLS, we treat the .m3u8 file as the main file, but we ideally need to upload the folder.
      // Current simple implementation: User selects .m3u8, we upload just that file.
      // TODO: Support folder upload or zip upload for HLS
      
      const videoUpload = await uploadToCOS({
        file: nextTask.file,
        onProgress: (percent) => {
          updateTask(nextTask.id, { progress: Math.round(percent * 0.5) });
        },
      });
      if (!videoUpload.success || !videoUpload.url) {
        throw new Error(videoUpload.error || "视频上传失败");
      }
      const videoUrl = videoUpload.url;
      addLog("第 1 步 (媒体)", "SUCCESS", "上传成功");
      updateTask(nextTask.id, { videoUrl, progress: 50 });

      // Step 2: Upload Thumbnail
      let thumbnailUrl = null;
      if (nextTask.thumbnailFile) {
        addLog("第 2 步 (封面图)", "WAIT", "上传中");
        const thumbnailUpload = await uploadThumbnailToCOS(nextTask.thumbnailFile);
        if (!thumbnailUpload.success || !thumbnailUpload.url) {
          throw new Error(thumbnailUpload.error || "封面上传失败");
        }
        thumbnailUrl = thumbnailUpload.url;
        addLog("第 2 步 (封面图)", "SUCCESS", "上传成功");
      } else {
        addLog("第 2 步 (封面图)", "SKIP", "无封面图");
      }
      updateTask(nextTask.id, { thumbnailUrl: thumbnailUrl || undefined, progress: 60 });

      // Step 3: Get Duration (with timeout fallback, avoid hanging at 60%)
      const durationSeconds = await getMediaDurationFromFile(nextTask.file);
      if (durationSeconds <= 0) {
        addLog("绗?2.5 姝?(鏃堕暱鎺㈡祴)", "SKIP", "鏃犳硶鑾峰彇鏃堕暱锛屽凡鎸?0 澶勭悊");
      }
      updateTask(nextTask.id, { progress: 70 });

      // Step 4: Create DB Entry
      addLog("第 3 步 (数据库同步)", "WAIT", "写入元数据");
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: videoData, error: dbError } = await supabase
        .from("videos")
        .insert({
          title: nextTask.metadata.title,
          description: nextTask.metadata.description,
          video_url: videoUrl,
          thumbnail_url: thumbnailUrl,
          duration: durationSeconds,
          jlpt_level: nextTask.metadata.jlpt_level as unknown as Database["public"]["Enums"]["jlpt_level"],
          jlpt_levels: nextTask.metadata.jlpt_levels || [nextTask.metadata.jlpt_level],
          category: nextTask.metadata.category,
          categories: nextTask.metadata.categories || [nextTask.metadata.category],
          is_premium: nextTask.metadata.is_premium,
          // Keep hidden until subtitle processing succeeds.
          is_published: false,
          created_by: user.id,
          series_id: nextTask.metadata.series_id === "none" ? null : nextTask.metadata.series_id,
        })
        .select()
        .single();

      if (dbError) throw dbError;
      createdVideoId = videoData.id;
      addLog("第 3 步 (数据库同步)", "SUCCESS", "写入成功");
      updateTask(nextTask.id, { progress: 80, status: 'processing' });

      // Step 5: Process Subtitles
      let tokensUsed = 0;
      let cost = 0;
      addLog("第 4 步 (AI处理)", "WAIT", "字幕生成与翻译中");

      if (nextTask.vttFile) {
        // Manual VTT upload
        const text = await nextTask.vttFile.text();
        const cues = parseVTT(text);
        if (cues.length === 0) {
          throw new Error("字幕文件解析失败：未识别到任何 VTT 段落");
        }
        const chunkSize = 8;
        const totalChunks = Math.ceil(cues.length / chunkSize);
        let usedRawFallback = false;

        try {
          for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const start = chunkIndex * chunkSize;
            const end = Math.min(cues.length, start + chunkSize);
            const chunk = cues.slice(start, end);

            const maxAttempts = 3;
            let chunkError: string | null = null;
            let chunkDone = false;

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
              const { error: subError } = await supabase.functions.invoke("process-vtt-subtitles", {
                body: {
                  videoId: videoData.id,
                  cues: chunk,
                  append: chunkIndex > 0,
                },
              });

              if (!subError) {
                chunkDone = true;
                break;
              }

              chunkError = await formatUploadError(subError);
              const canRetry = attempt < maxAttempts && isRetryableEdgeFunctionError(chunkError);
              if (!canRetry) break;

              addLog(
                "第 4 步 (AI处理)",
                "RUNNING",
                `第 ${chunkIndex + 1}/${totalChunks} 批超时，重试 ${attempt}/${maxAttempts - 1}`,
              );
              await sleep(1200 * attempt);
            }

            if (!chunkDone) {
              throw new Error(`字幕处理失败（第 ${chunkIndex + 1}/${totalChunks} 批）：${chunkError || "未知错误"}`);
            }

            const chunkProgress = totalChunks <= 1 ? 90 : 80 + Math.round(((chunkIndex + 1) / totalChunks) * 15);
            updateTask(nextTask.id, { progress: Math.max(80, Math.min(95, chunkProgress)) });
          }
        } catch (subtitlesError) {
          const detail = await formatUploadError(subtitlesError);
          if (!isRetryableEdgeFunctionError(detail)) {
            throw subtitlesError;
          }

          addLog("第 4 步 (AI处理)", "ERROR", `Edge 超时，降级为原始字幕：${detail}`);
          await saveRawVttSubtitles(videoData.id, cues);
          usedRawFallback = true;
          updateTask(nextTask.id, { progress: 95 });
        }

        if (usedRawFallback) {
          addLog("第 4 步 (AI处理)", "DONE", "已保存原始字幕（可后续补翻译）");
        } else {
          addLog("第 4 步 (AI处理)", "SKIP", "使用本地字幕");
        }
      } else {
        // Auto AI processing
        const { data: aiData, error: aiError } = await supabase.functions.invoke("process-video", {
          body: {
            videoId: videoData.id,
            videoUrl: videoUrl,
            title: nextTask.metadata.title,
            autoDetectLevel: nextTask.metadata.autoDetectLevel
          }
        });
        if (aiError) {
          const detail = await formatUploadError(aiError);
          throw new Error(`AI 处理失败：${detail}`);
        }
        if (aiData?.error) {
          throw new Error(`AI 处理失败：${String(aiData.error)}`);
        }
        
        tokensUsed = aiData?.tokensUsed || 0;
        cost = aiData?.cost || 0;
        addLog("第 4 步 (AI处理)", "DONE", `成功 (LLM) - ${aiData?.subtitlesCount || 0} 条字幕`);
      }

      if (requestedPublishState) {
        const { error: publishError } = await supabase
          .from("videos")
          .update({ is_published: true })
          .eq("id", videoData.id);
        if (publishError) throw publishError;
        addLog("第 5 步 (发布状态)", "SUCCESS", "已发布");
      } else {
        addLog("第 5 步 (发布状态)", "SKIP", "保持未发布");
      }

      const { count: subtitleCount } = await supabase
        .from("subtitles")
        .select("id", { count: "exact", head: true })
        .eq("video_id", videoData.id);

      logs.push("----------------------------");
      if (tokensUsed > 0) {
        logs.push(`[${new Date().toLocaleString('zh-CN', { hour12: false })}] [AUDIT] 本次任务 Token 消耗: ${tokensUsed} (约 $${cost.toFixed(4)})`);
      }

      // Create final log entry in DB
      const finalLog = logs.join("\n");
      await supabase.from("upload_logs").insert({
        video_id: videoData.id,
        uploaded_by: user.id,
        file_name: nextTask.file.name,
        file_size: nextTask.file.size,
        file_type: nextTask.file.type,
        status: 'completed',
        duration_ms: Date.now() - startTime.getTime(),
        cos_key: videoUrl.split('/').pop(),
        cdn_url: videoUrl,
        subtitle_count: subtitleCount || 0,
        subtitle_source: nextTask.vttFile ? 'vtt' : 'ai',
        metadata: {
          detailed_log: finalLog,
          title: nextTask.metadata.title
        }
      });

      updateTask(nextTask.id, { status: 'completed', progress: 100, logs: [...logs] });
      toast({ title: `视频 "${nextTask.metadata.title}" 上传成功` });

    } catch (error: unknown) {
      console.error("Upload failed:", error);
      const message = await formatUploadError(error);

      if (createdVideoId) {
        try {
          await supabase
            .from("videos")
            .update({ is_published: false })
            .eq("id", createdVideoId);
        } catch (publishRollbackError) {
          console.error("Failed to rollback publish status:", publishRollbackError);
        }
      }
      
      // Log error
      logs.push(`[ERROR] ${message}`);
      const finalLog = logs.join("\n");
      
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
         await supabase.from("upload_logs").insert({
          video_id: createdVideoId,
          uploaded_by: user.id,
          file_name: nextTask.file.name,
          file_size: nextTask.file.size,
          file_type: nextTask.file.type,
          status: 'failed',
          duration_ms: Date.now() - startTime.getTime(),
          subtitle_source: nextTask.vttFile ? 'vtt' : 'ai',
          error_message: message,
          metadata: {
            detailed_log: finalLog,
            title: nextTask.metadata.title
          }
        });
      }

      updateTask(nextTask.id, { 
        status: 'error', 
        error: message,
        logs: [...logs],
      });
      toast({ 
        title: `视频 "${nextTask.metadata.title}" 上传失败`, 
        description: message,
        variant: "destructive" 
      });
    } finally {
      currentUploadRef.current = null;
      setIsUploading(false);
    }
  }, [tasks, isUploading, toast]);

  // Watch for started tasks
  useEffect(() => {
    if (autoStart && !isUploading) {
      processNextTask();
    }
  }, [tasks, isUploading, processNextTask, autoStart]);

  return (
    <UploadContext.Provider value={{ tasks, addTask, startTask, startAll, removeTask, retryTask, clearCompleted, isUploading }}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUploadQueue() {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error('useUploadQueue must be used within an UploadProvider');
  }
  return context;
}
